import dayjs from 'dayjs';
import { QUERY_EXIT_TYPE, QUERY_REMAINING_CAREGIVEES } from '../constants/routes';

export const isSSR = (): boolean => typeof window === 'undefined';
export const isProd = (): boolean => process.env.ENV === 'prod';

export const getCurrentEnv = (): string => {
  if (isSSR()) {
    if (process.env?.ENV === null || process.env?.ENV === undefined) {
      if (process?.env?.NEXT_PUBLIC_APP_ENV === 'lighthouse' || process?.env?.NEXT_PUBLIC_APP_ENV === 'pa11y') {
        return 'QA2';
      } else {
        return '';
      }
    }
    const environment = process.env.ENV.toUpperCase();
    switch (process.env.ENV) {
      case 'local':
        return 'LOCAL';
      case 'prod':
      case 'prod-west':
        return 'PROD';
      case 'prod-es':
        return 'PROD-ES';
      default:
        return environment ? environment : 'PROD';
    }
  }
  const host = window?.location?.hostname;
  const prodHost = ['m.cvs.com', 'www.cvs.com'];
  if (prodHost.indexOf(host) > -1) return 'PROD';
  if (host === 'es.cvs.com') return 'PROD-ES';
  if (host === 'es-qa2.cvs.com') return 'ES-QA2';
  let env = 'QA2';
  if (host.includes('localhost')) {
    env = 'LOCAL';
  }
  if (host.includes('qa-csr')) {
    env = 'QA2-CSR';
  } else if (host.includes('preview')) {
    env = 'PREVIEW';
  } else if (host?.split('-')?.length > 1) {
    env = host.split('-')[1]?.split('.')[0]?.toUpperCase();
  }
  return env ? env : 'PROD';
};

export const isCVSMobileApp = (): boolean | undefined => {
  if (typeof window !== 'undefined') {
    if (
      window?.navigator?.userAgent.match(/CVS_iPAD_APP/i) ||
      window?.navigator?.userAgent.match(/CVS_ANDROID_APP/i) ||
      window?.navigator?.userAgent.match(/CVS_iPHONE_APP/i)
    ) {
      return true;
    }
    return false;
  }
};

export const formatTel = (e: any | FocusEvent, ref: any): void => {
  const el = ref.current.children[2];
  let pos = el.selectionStart;
  const skip = new Set([0, 1, 4, 5, 9]);
  const tel = el.value
    .replace('(', '')
    .replace(')', '')
    .replace('-', '')
    .replace(/^\+?[0-1]/g, '')
    .replace(/\s/g, '');
  if (!tel) {
    el.value = '';
  } else {
    if (e.nativeEvent.inputType === 'deleteContentBackward') return;
    if (tel.length <= 2) {
      el.value = `(${tel.substring(0)})`;
    }
    if (tel.length >= 3 && tel.length <= 5) {
      el.value = `(${tel.substring(0, 3)}) ${tel.substring(3)}`;
    }
    if (tel.length >= 6) {
      el.value = `(${tel.substring(0, 3)}) ${tel.substring(3, 6)}-${tel.substring(6, 10)}`;
    }
    if (pos === 10 || pos === 6) {
      pos += 1;
    }
    if (pos === 5) {
      pos += 2;
    }
    while (skip.has(pos)) {
      pos += 1;
    }
    if (!('inputType' in e.nativeEvent)) return;
    el.selectionStart = pos;
    el.selectionEnd = pos;
  }
};

export const onlyNumberKey = (event: any) => {
  // allow copy, paste, select all etc.
  if (event.metaKey || event.controlKey) {
    return;
  }
  // allow shift, delete, tab etc.
  if (event.key.length > 1) return;
  // don't allow any key except numbers.
  const regex = /[0-9]/;
  if (!regex.test(event.key)) event.preventDefault();
};

export const formatDate = (e: any | FocusEvent, ref: any): void => {
  if (e.nativeEvent.inputType === 'deleteContentBackward') return;
  const el = ref.current.children[2];
  const dob = el.value.replace(/\//g, '');
  if (dob.length < 2) return;
  if (dob.length < 4) {
    el.value = [dob.substring(0, 2), dob.substring(2, 4)].join('/');
  } else {
    el.value = [dob.substring(0, 2), dob.substring(2, 4), dob.substring(4, 8)].join('/');
  }
};
// convert date from MMDDYYYY to YYYY-MM-DD
export const convertDateFormatForAPI = (value: string) => {
  const date = value.split('/');
  return `${date[2]}-${date[0]}-${date[1]}`;
};

// convert raw MMDDYYYY digits (single-field dob input) to YYYY-MM-DD
export const convertRawDateFormatForAPI = (value: string) => {
  if (!value) return '';
  return `${value?.substring(4, 8)}-${value?.substring(0, 2)}-${value?.substring(2, 4)}`;
};

// add 0s to the store number to make them 5 digit in API call
export const convertStoreNumberFormatForAPI = (value: string) => {
  if (value?.length < 4) value = '00' + value;
  else if (value?.length < 5) value = '0' + value;
  return value;
};

export const convertPhoneFormatForAPI = (value: string) => {
  return value
    .replace('(', '')
    .replace(')', '')
    .replace('-', '')
    .replace(/^\+?[0-1]/g, '')
    .replace(/\s/g, '');
};

export const getOrigin = (): string => {
  const env = getCurrentEnv();
  if (env == 'LOCAL') {
    return 'https://www-qa2.cvs.com';
  }
  return `https://www${env !== 'prod' ? '-' + env : ''}.cvs.com`;
};

export const checkReturnUrl = (url: string) => {
  // Check if the returnUrl is given as full url or path only
  if (url?.indexOf('http://') === 0 || url?.indexOf('https://') === 0) {
    const testURL = new URL(url);
    let host = '';
    if (testURL) host = testURL.host;
    // URL interface is not supported by Internet explorer yet
    // so added below logic for testURL undefined case at IE
    else {
      const l = document.createElement('a');
      l.href = url;
      host = l.hostname;
    }
    // check if host includes cvs.com
    if (host && host.includes('.cvs.com')) {
      return url;
      // if host doesn't include cvs.com then ignore it and redirect to home page
    } else return '/';
    // if it's not full url then return the path directly
  } else return url;
};

// TODO: clean up the fn after landing page switch
export const navigateBackToCallingApp = () => {
  setTimeout(() => {
    const entryPointForLookup = sessionStorage.getItem('postReturnUrl') || '/';
    const url = checkReturnUrl(entryPointForLookup);
    url && window.location.assign(url);
  }, 500);
};

export const navigateToLogin = () => {
  const loginUrl = `${window.origin}/account-login/look-up?returnUrl=/account/manage-family`;
  const url = checkReturnUrl(loginUrl);
  url && window.location.assign(url);
};

export const navigateToCaregiverBlockedPage = () => {
  setTimeout(() => {
    const returnUrl = window.sessionStorage.getItem('postLockoutUrl');
    const bkpUrl = `${window.origin}/pharmacy`;
    const finalUrl = checkReturnUrl(returnUrl ? returnUrl : bkpUrl);
    window?.location.replace(finalUrl);
  }, 500);
};

export const isMobile = (): boolean =>
  isSSR() ? false : window.navigator.userAgent.toLowerCase().indexOf('mobi') !== -1;

export const formatBirthdate = (inputDate: string) => {
  // Parse the input date
  const date = new Date(inputDate);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return '';
  }

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  // Get day, month, and year from the parsed date
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const formattedDate = `${month} ${day}, ${year}`;

  return formattedDate;
};

// Make first letter capitial for a word
export const capitalizeFirstLetter = (word: string): string => {
  if (word && word.trim() !== '') {
    word = word?.toLowerCase();
    return word.charAt(0).toUpperCase() + word.slice(1);
  }
  return word;
};

// Return full name with first letters in capital
export const nameCreator = (firstName: string, lastName: string): string => {
  return capitalizeFirstLetter(firstName) + ' ' + capitalizeFirstLetter(lastName);
};

// Return masked email
export const maskEmail = (email) => {
  email = email?.replace(/^(.)(.*)(.@.*)$/, (_, a, b, c) => a + b.replace(/./g, '*') + c);
  return email;
};

export const getDeviceType = (): string => {
  // Other
  let deviceType = 'DESKTOP';
  const ua: string = window.navigator.userAgent;

  // Android Specific Checks
  if (/Android/i.test(ua)) {
    // Android Mobile
    if (/Mobile/i.test(ua)) {
      deviceType = 'AND_MOBILE';
    } else if (/Glass/i.test(ua)) {
      // Android Glass
      deviceType = 'AND_GLASS';
    } else {
      // Android Tablet
      deviceType = 'AND_TABLET';
    }
  } else if (/iPhone|iPod/i.test(ua)) {
    // iOS Mobile
    deviceType = 'IOS_MOBILE';
  } else if (/iPad/i.test(ua)) {
    // iOS Tablet
    deviceType = 'IOS_TABLET';
  } else if (/IEMobile/i.test(ua)) {
    // Windows
    deviceType = 'WIN_MOBILE';
  } else if (/webOS|BlackBerry|Opera Mini/i.test(ua)) {
    // Other Identified Vendor
    deviceType = 'OTH_MOBILE';
  }
  return deviceType;
};

//Clear sessionStorage related to redirection once addCaregivee is successful
export const removeRedirectionValues = () => {
  sessionStorage.removeItem('postReturnUrl');
};

// removes rxhistory from session storage for /details API to be invoked again on rxcenter page
export const removeRxHistory = () => {
  sessionStorage.removeItem('rxhistory');
};

export const testJSON = (text) => {
  if (text && typeof text !== 'string') {
    return false;
  }
  try {
    const json = JSON.parse(text);
    return typeof json === 'object';
  } catch (error) {
    return false;
  }
};

export interface xidInfoProps {
  token: string;
  tokenId: string;
  name: string;
}

export const addZero = (event: any) => {
  if (event.target.value < 1) return;
  if (event.target.value.length < 2) event.target.value = 0 + event.target.value;
};


export const compareToDaysLeftForMinorToTurn18 = (compareTo, birthdate) => {
  const today = dayjs().format('YYYY-MM-DD');
  const years = dayjs().diff(birthdate, 'years');
  const above17Below18 = years >= 17 && years < 18;
  if (above17Below18) {
    // Special case if birthday is today; we do NOT need an extra year added
    const adjustToday = birthdate.substring(5) === today.substring(5) ? 0 : 1;
    // Add age plus one year (unless birthday is today) to get next birthday
    const nextBirthday = dayjs(birthdate)
      .clone()
      .add(years + adjustToday, 'years');
    // Final calculation in days
    const daysUntilBirthday = nextBirthday.diff(today, 'day');

    return daysUntilBirthday === compareTo;
  }
  return above17Below18;
};

// Join names from an array to a string
export const compileNameList = (nameArray) => {
  if (nameArray?.length === 0) {
    return '';
  }
  if (nameArray?.length === 1) {
    return nameArray[0].firstName;
  }

  if (nameArray?.length === 2) {
    return `${nameArray[0].firstName} and ${nameArray[1].firstName}`;
  }

  if (nameArray?.length > 2) {
    return (
      nameArray
        .slice(0, -1)
        .map((arr) => {
          return `${arr?.firstName}`;
        })
        .join(', ') + ` and ${nameArray[nameArray.length - 1]?.firstName}`
    );
  }
};

type Provision = { type: string; actions?: string[] };
type ConsentItem = { lob?: string; provisions?: Provision[] };
type ConsentGroup = { type: string; consents?: ConsentItem[] };

export const getConsentsList = (consentsList: ConsentGroup[]): string[] => {
  const providedConsentLists: string[] = [];
  if (consentsList?.length > 0) {
    const xlobConsentList = consentsList.find((consent) => consent.type === 'XLOB');
    if (xlobConsentList?.consents?.length > 0) {
      const cmkConsent = xlobConsentList.consents?.find((consent) => consent.lob === 'Caremark');
      if (cmkConsent?.provisions?.[0]?.type === 'allow') {
        providedConsentLists.push('PBM');
      }
    }
  }
  return providedConsentLists;
};

export const showDobPage = (contact: any): boolean => {
  if (!contact?.source || contact.source === 'PBM') return false; // is PBM
  if (contact.source === 'RX' || contact.caregiveeType === 'Pet') return true;
  return false;
};

export const filterContactsByConsents = (
  contacts: any[],
  consents: string[],
  numberOfItems?: number
): any[] => {
  const result: any[] = [];

  for (const contact of contacts ?? []) {
    const source = contact?.source;
    const sourceSystems: string[] = contact?.sourceSystems ?? [];
    const noSourceInfo = !source;

    if (noSourceInfo) {
      if (consents.includes('PBM')) result.push(contact);
    } else if (consents.includes('PBM') && source === 'PBM') {
      result.push(contact);
    } else if (source === 'RX') {
      result.push(contact);
    } else if (!consents.includes('PBM') && source === 'PBM' && sourceSystems.includes('RX')) {
      result.push(contact);
    }
  }

  return numberOfItems !== undefined ? result.slice(0, numberOfItems) : result;
};

export const buildExitUrl = (url: string, exitType?: string, count?: number): string => {
  const parts: string[] = [];
  if (count !== undefined) parts.push(`${QUERY_REMAINING_CAREGIVEES}=${count}`);
  if (exitType) parts.push(`${QUERY_EXIT_TYPE}=${exitType}`);
  if (!parts.length) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${parts.join('&')}`;
};

export const deriveUserType = (caregiveeType: string, hasContact: string): string => {
  const type = caregiveeType?.toLowerCase() || '';
  return hasContact === 'false' && type === 'adult' ? 'adult-email' : type;
};

export const getLDQueryParams = () => {
  if (isSSR()) {
    return;
  }
  const searchParam = window?.location?.search;
  const urlParams = new URLSearchParams(searchParam);
  return urlParams.get('location');
};

export const getMfaEnvValue = () => {
  const currentEnv = getCurrentEnv();
   if (currentEnv === 'PROD-ES') {
      return 'ESP';
   }

   if (currentEnv === 'ES-QA2') {
      return 'ESPQA2';
   }

   return currentEnv === 'LOCAL' ? 'qa2' : currentEnv.toLowerCase();
};